import {
  type TextFieldProps,
  normalizeNumberInput,
  formatNumberInput,
  parseNumberInput,
  useNumberDraft,
  TextField,
  get,
} from '@mfw/components';
import {
  type ControllerRenderProps,
  type RegisterOptions,
  Controller,
  type FieldError,
} from 'react-hook-form';

export interface FormTextFieldProps extends Omit<
  TextFieldProps,
  'errors' | 'type'
> {
  type?: 'number' | TextFieldProps['type'];
  rules?: RegisterOptions;
  required?: boolean;
}

// `name`, like `value` and `onBlur`, arrives with the spread `field`
type NumericTextFieldProps = Omit<TextFieldProps, 'name' | 'type' | 'value'> & {
  field: ControllerRenderProps;
};

/**
 * Numeric variant of the field. The form value is a number, but the input has
 * to remember the text as typed: `175,` and `1,50` are legitimate intermediate
 * states that no number can represent. Feeding the parsed number straight back
 * as `value` is what made decimals impossible to enter — React resets the DOM
 * value on every keystroke for `type="text"` (it compares strings), so the
 * separator was erased the moment it was typed.
 */
const NumericTextField = ({
  field,
  inputMode = 'decimal',
  onChange,
  onBlur,
  ...otherProps
}: NumericTextFieldProps) => {
  const { draft, setDraft, locale } = useNumberDraft(field.value);

  return (
    <TextField
      {...field}
      {...otherProps}
      onChange={(event) => {
        const next = normalizeNumberInput(event.target.value, locale);
        setDraft(next);
        field.onChange(parseNumberInput(next, locale));
        if (onChange) {
          onChange(event);
        }
      }}
      onBlur={(event) => {
        // tidy the intermediate spelling ("175," / "1,50" / "-") the user left
        // behind; the parsed value was already emitted per keystroke
        const parsed = parseNumberInput(draft, locale);
        setDraft(parsed === null ? '' : formatNumberInput(parsed, locale));
        field.onBlur();
        onBlur?.(event);
      }}
      inputMode={inputMode}
      value={draft}
      type="text"
      ref={field.ref}
    />
  );
};

export const FormTextField = ({
  defaultValue,
  fullWidth = true,
  multiline,
  name,
  onChange,
  required,
  rules,
  type,
  ...otherProps
}: FormTextFieldProps) => {
  return (
    <Controller
      render={({ field, formState: { defaultValues, errors, isLoading } }) => {
        const formDefaultValue = get<string>(defaultValues, name) ?? undefined;
        const error = get<FieldError>(errors, name) ?? undefined;

        if (type === 'number') {
          return (
            <NumericTextField
              {...otherProps}
              loading={otherProps?.loading || isLoading}
              required={required ?? false}
              errors={error?.message}
              multiline={multiline}
              fullWidth={fullWidth}
              onChange={onChange}
              field={field}
            />
          );
        }

        return (
          <TextField
            defaultValue={formDefaultValue}
            required={required ?? false}
            errors={error?.message}
            fullWidth={fullWidth}
            multiline={multiline}
            {...field}
            // NOTHING is rewritten here, `type="email"` included. Rewriting
            // `event.target.value` mid-keystroke moves the caret, and an email
            // input cannot put it back — it does not support the selection API
            // — so typing a leading space used to scramble the whole address.
            // Whitespace on an address is normalized by the schema instead
            // (`z.string().trim().email()`), which also covers the callers that
            // never went through this component.
            onChange={(e) => {
              field.onChange(e);
              if (onChange) {
                onChange(e);
              }
            }}
            type={type}
            // null iz RHF (prefill sa API-ja) ne sme do DOM inputa — React
            // warning i prelaz na uncontrolled; prazan string = isti prikaz
            value={field.value ?? ''}
            {...otherProps}
            loading={otherProps?.loading || isLoading}
            ref={field.ref}
          />
        );
      }}
      rules={required ? { required: true, ...rules } : rules}
      name={name}
    />
  );
};
